import { useEffect, useState } from 'react'
import type { Alert, AlertSeverity, AlertStatus } from '../types/portfolio-manager'

const API_BASE = 'http://localhost:5001/api/portfolio-manager'

const SEVERITY_COLORS: Record<AlertSeverity, { bg: string; border: string; dot: string; text: string }> = {
  critical: { bg: 'bg-red-900/30', border: 'border-red-700', dot: 'bg-red-500', text: 'text-red-400' },
  warning: { bg: 'bg-yellow-900/30', border: 'border-yellow-700', dot: 'bg-yellow-500', text: 'text-yellow-400' },
  info: { bg: 'bg-blue-900/30', border: 'border-blue-700', dot: 'bg-blue-500', text: 'text-blue-400' },
}

const STATUS_LABELS: Record<AlertStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  dismissed: 'Dismissed',
  snoozed: 'Snoozed',
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [severityFilter, setSeverityFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('open')

  const fetchAlerts = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.set('status', statusFilter)
      if (severityFilter !== 'all') params.set('severity', severityFilter)

      const res = await fetch(`${API_BASE}/alerts?${params}`)
      if (!res.ok) throw new Error('Failed to fetch alerts')
      const data = await res.json()
      setAlerts(data.alerts || [])
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAlerts()
  }, [severityFilter, statusFilter])

  const updateAlertStatus = async (alertId: string, newStatus: string) => {
    try {
      const res = await fetch(`${API_BASE}/alerts/${alertId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      })
      if (!res.ok) throw new Error('Failed to update alert')
      // Refresh alerts
      fetchAlerts()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const severityCounts = {
    all: alerts.length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    info: alerts.filter(a => a.severity === 'info').length,
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Alerts</h1>
        <button
          onClick={fetchAlerts}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* Severity Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Severity:</span>
          <div className="flex gap-1">
            {(['all', 'critical', 'warning', 'info'] as const).map((sev) => (
              <button
                key={sev}
                onClick={() => setSeverityFilter(sev)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  severityFilter === sev
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
                {sev === 'all' ? '' : ` (${severityCounts[sev]})`}
              </button>
            ))}
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-md px-3 py-1 text-sm text-white"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="dismissed">Dismissed</option>
            <option value="snoozed">Snoozed</option>
          </select>
        </div>
      </div>

      {/* Alert Cards */}
      {loading ? (
        <div className="text-gray-400 text-center py-12">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="text-gray-400 text-center py-12 bg-gray-800 border border-gray-700 rounded-lg">
          <p className="text-lg">No alerts found</p>
          <p className="text-sm mt-1">
            {statusFilter === 'open'
              ? 'No open alerts — your portfolio looks good!'
              : 'No alerts match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info
            return (
              <div
                key={alert.id}
                className={`${colors.bg} border ${colors.border} rounded-lg p-4`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                      <span className={`text-xs font-medium uppercase ${colors.text}`}>
                        {alert.severity}
                      </span>
                      {alert.ticker && (
                        <span className="text-xs bg-gray-700 px-2 py-0.5 rounded text-gray-300">
                          {alert.ticker}
                        </span>
                      )}
                      {alert.accountSlug && (
                        <span className="text-xs bg-gray-700 px-2 py-0.5 rounded text-gray-300">
                          {alert.accountSlug}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {STATUS_LABELS[alert.status]}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-white">{alert.title}</h3>
                    <p className="text-sm text-gray-300 mt-1">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(alert.createdAt).toLocaleString()}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  {alert.status === 'open' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => updateAlertStatus(alert.id, 'acknowledged')}
                        className="px-3 py-1 bg-blue-700 hover:bg-blue-600 rounded text-xs text-white transition-colors"
                        title="Acknowledge"
                      >
                        ✓ Ack
                      </button>
                      <button
                        onClick={() => updateAlertStatus(alert.id, 'snoozed')}
                        className="px-3 py-1 bg-yellow-700 hover:bg-yellow-600 rounded text-xs text-white transition-colors"
                        title="Snooze"
                      >
                        💤 Snooze
                      </button>
                      <button
                        onClick={() => updateAlertStatus(alert.id, 'dismissed')}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-white transition-colors"
                        title="Dismiss"
                      >
                        ✕ Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
